import { Component, DestroyRef, ElementRef, inject, Input, OnInit } from '@angular/core';
import { CampaignDrawerGroupBase } from '../campaign-drawer-base/campaign-drawer-group-base';
import { CommonModule, DOCUMENT } from '@angular/common';
import { distinctUntilChanged, filter, map, startWith } from 'rxjs/operators';
import { CampaignDrawerOpen, DEFAULT_DRAWER_STATE } from '@finder/shared/services/campaign-drawer/campaign-drawer.interface';
import { CREATE_STYLE, CreateStyleProvider } from '@finder/shared/utils/dom/createStyle';
import { CampaignDrawerService } from '@finder/shared/services/campaign-drawer/campaign-drawer.service';
import { fromEvent, Observable } from 'rxjs';
import { BREAKPOINTS, ViewportDetectionService } from '@finder/shared/services/viewport-detection/viewport-detection.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DrawerCardsGap, DrawerGroupMultiRowData } from '@finder/shared/interfaces/shared.interface';
import { Tab } from '../tab/tab.interface';
import { ComponentHeaderComponent } from '../component-header/component-header.component';
import { CampaignDrawerCardComponent } from '../campaign-drawer-card/campaign-drawer-card.component';
import { CampaignDrawerDetailComponent } from '../campaign-drawer-detail/campaign-drawer-detail.component';
import { WindowRef } from '@finder/shared/services/window-ref/window-ref.service';

const RES_MAX_ITEMS = {
    MOBILE: 2,
    DESKTOP: 4
};

@Component({
    standalone: true,
    selector: 'finder-campaign-drawer-group-multi-row',
    templateUrl: './campaign-drawer-group-multi-row.component.html',
    styleUrls: ['./campaign-drawer-group-multi-row.component.scss'],
    imports: [
        ComponentHeaderComponent,
        CampaignDrawerCardComponent,
        CommonModule,
        CampaignDrawerDetailComponent
    ],
})
export class CampaignDrawerGroupMultiRowComponent extends CampaignDrawerGroupBase implements OnInit {
    @Input() public groupData: DrawerGroupMultiRowData;
    @Input() public tabChange?: Observable<Tab>;

    public css: any;
    public selectedCard = -1;
    public drawerRows: any[] = [];
    private cardData: any;

    private destroyRef: DestroyRef = inject(DestroyRef);
    private viewportService: ViewportDetectionService = inject(ViewportDetectionService);
    private document: Document = inject(DOCUMENT);
    private windowRef: WindowRef = inject(WindowRef);
    private createStyle: CreateStyleProvider = inject(CREATE_STYLE);
    protected drawerService: CampaignDrawerService = inject(CampaignDrawerService);
    private el = inject(ElementRef);

    ngOnInit(): void {
        this.generateCss();

        // Set the groupId so the caret shows
        this.setGroupId(this.groupData.blockId);

        this.createRowsByResolution();

        this.onDataChange
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                filter((data: CampaignDrawerOpen) => data !== null),
            )
            .subscribe({
                next: (data: CampaignDrawerOpen) => this.dataChange(data)
            });

        // If this component is inside Tab component
        // we support tabChange feature to close drawer when triggered
        if (this.tabChange) {
            this.tabChange
                .pipe(
                    takeUntilDestroyed(this.destroyRef)
                ).subscribe({
                    next: () => this.drawerService.setOpen(DEFAULT_DRAWER_STATE)
                });
        }
    }

    private createRowsByResolution() {
        let windowRef = this.windowRef.nativeWindow;

        // Create card rows based on screen resolution
        fromEvent(windowRef, 'resize').pipe(
            takeUntilDestroyed(this.destroyRef),
            map(() => windowRef.innerWidth),
            startWith(windowRef.innerWidth),
            distinctUntilChanged((prev: number, curr: number) => {
                // Prevent re-creating rows if resolution does not cross breakpoint
                const prevState = prev >= BREAKPOINTS.tablet;
                const currState = curr >= BREAKPOINTS.tablet;

                return prevState === currState;
            })).subscribe({
            next: () => {
                this.setDrawerRows();
                this.dataChange(this.cardData);
            }
        });
    }

    // Set the order to place correctly the drawer detail when data changes
    private dataChange(data: CampaignDrawerOpen): void {
        if (!data) {
            return;
        }

        this.cardData = data;

        const isTablet: boolean = this.viewportService.isMobile();
        const maxItems: number = isTablet ? RES_MAX_ITEMS.MOBILE : RES_MAX_ITEMS.DESKTOP;

        this.css = { order: Math.floor((this.cardData.cardIndex - 1) / maxItems) };
    }

    private generateCss(): void {
        if (!this.groupData) {
            return;
        }

        const blockId: number = this.groupData.parentBlockId ?? this.groupData.blockId ?? 0;
        const drawers: any[] = this.groupData.drawerGroup ?? [];
        const maxPossibleRows: number = Math.ceil(drawers.length / RES_MAX_ITEMS.MOBILE);
        const cssArray: string[] = [];

        // Create order for the rows
        for (let index = 0; index < maxPossibleRows; index++) {
            const rowNumber: number = index;
            cssArray.push(`
                .blockId-${blockId} .drawer-group-multi-row .drawer-group-row:nth-child(${rowNumber + 2}) {
                    order: ${rowNumber};
                }`
            );
        }

        // Set gap between cards
        const cardsGap: DrawerCardsGap = this.groupData.cardsGap;

        // If we are in a tab component all drawers are rendered but hidden
        // We need to identify each parent for each instance and apply gap separetly
        // We use the parentId to apply gap only for one container
        const parent: HTMLElement = this.el.nativeElement.parentElement;
        const parentId: string = parent.id !== '' ? '#' + parent.id : '';

        cssArray.push(`@media (min-width: ${BREAKPOINTS.tablet}px) {
                .blockId-${blockId} ${parentId} .drawer-group-multi-row .drawer-group-row {
                    &:nth-child(2) {
                        gap: ${cardsGap.topRow}px;
                    }
                    &:nth-child(3) {
                        gap: ${cardsGap.bottomRow}px;
                    }
                }
            }`
        );

        // Add css to the page
        this.createStyle(this.document, cssArray.join(''));
    }

    public getFullCardIndex(rowIndex: number, cardIndex: number): number {
        const isTablet: boolean = this.viewportService.isMobile();

        return rowIndex * (isTablet ? RES_MAX_ITEMS.MOBILE : RES_MAX_ITEMS.DESKTOP) + cardIndex;
    }

    private setDrawerRows(): void {
        if (!this.groupData) {
            return;
        };

        const drawers: any[] = this.groupData.drawerGroup ?? [];
        const totalDrawers: number = drawers.length;
        const maxItems: number = this.groupData.count ?? RES_MAX_ITEMS.DESKTOP;
        const largerRow: string = this.groupData.largerRow ?? 'top';
        const isTablet: boolean = this.viewportService.isMobile();
        const rows: any[] = [];

        // Define how many items per row we want depending on device
        const itemsPerRow: number = isTablet ? RES_MAX_ITEMS.MOBILE : maxItems;

        if (!isTablet && totalDrawers <= RES_MAX_ITEMS.DESKTOP) {
            // If we have a desktop resolution
            // and the amount of drawers are less than max
            // we allocate all of them in the same row
            rows.push(drawers);
        } else {
            // We divide the drawers depending on the max amount
            for (let i = 0; i < totalDrawers; i += itemsPerRow) {
                rows.push(drawers.slice(i, i + itemsPerRow));
            }

            // If we have 5 or 7 drawers on desktop we check wich row is bigger with largerRow
            if (!isTablet && (totalDrawers === 5 || totalDrawers === 7) && largerRow === 'bottom') {
                const lastFromTop = rows[0].pop();
                rows[1].unshift(lastFromTop);
            }
        }

        this.drawerRows = [...rows];
    }

}
